import { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import PhotosUploader from "../../components/Forms/ImageUploader";

export default function Form({
  fetchquestions,
  closeDialog,
}: {
  fetchquestions: () => void;
  closeDialog?: () => void;
}) {
  const [title, setTitle] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [answer, setAnswer] = useState(-1);
  const [adding, setAdding] = useState(false);
  
  // New states for image-based questions
  const [questionType, setQuestionType] = useState<'text' | 'image'>('text');
  const [images, setImages] = useState<string[]>([]);
  const [imageDisplayDuration, setImageDisplayDuration] = useState(30);

  const validateForm = () => {
    if (title === "" || options.includes("") || answer === -1) {
      return false;
    }
    if (questionType === 'image' && images.length === 0) {
      return false;
    }
    return true;
  };

  const isAnyOptionEmpty = () => {
    return options.includes("");
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("All fields are required");
      return;
    }
    setAdding(true);
    try {
      const questionData: any = {
        title,
        options,
        answer,
        questionType
      };

      // Add image-specific fields if it's an image question
      if (questionType === 'image') {
        questionData.imageUrl = images[0];
        questionData.imageDisplayDuration = imageDisplayDuration;
      }

      await axios.post(
        `${process.env.REACT_APP_BASE_URI}/question`,
        questionData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      fetchquestions();
      toast.success("Question added successfully");
      
      // Reset form
      setAnswer(-1);
      setTitle("");
      setOptions(["", "", "", ""]);
      setQuestionType('text');
      setImages([]);
      setImageDisplayDuration(30);
      if (closeDialog) {
        closeDialog();
      }
    } catch (error) {
      console.log(error);
      toast.error("An error occurred");
    } finally {
      setAdding(false);
    }
  };

  const getAnswerByIndex = (index: number) => {
    return options[index];
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-2">Add a Question</h2>
        <p className="text-gray-600 mb-6">Fill in the form below to add a new question</p>
        <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="questionType" className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type
                </label>
                <select
                  id="questionType"
                  value={questionType}
                  onChange={(e) => setQuestionType(e.target.value as 'text' | 'image')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">Text Question</option>
                  <option value="image">Image-based Question</option>
                </select>
              </div>

              {questionType === 'image' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question Image
                    </label>
                    <PhotosUploader
                      addedPhotos={images}
                      onChange={setImages}
                      maxPhotos={1}
                    />
                  </div>
                  <div>
                    <label htmlFor="imageDisplayDuration" className="block text-sm font-medium text-gray-700 mb-1">
                      Image Display Duration (seconds)
                    </label>
                    <input
                      id="imageDisplayDuration"
                      type="number"
                      min="5"
                      max="300"
                      value={imageDisplayDuration}
                      onChange={(e) => setImageDisplayDuration(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Time in seconds the image will be visible during the test
                    </p>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Question Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your question"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                {options.map((option, index) => (
                  <input
                    key={index}
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...options];
                      newOptions[index] = e.target.value;
                      setOptions(newOptions);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    placeholder={`Option ${index + 1}`}
                  />
                ))}
              </div>
              <div>
                <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">
                  Correct Answer
                </label>
                <select
                  id="answer"
                  value={getAnswerByIndex(answer)}
                  onChange={(e) => {
                    let optionNumber = options.findIndex(option => option === e.target.value);
                    setAnswer(optionNumber);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select the correct answer</option>
                  {!isAnyOptionEmpty() &&
                    options.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button 
                  type="button"
                  onClick={() => {
                    if (closeDialog) {
                      closeDialog();
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={adding}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {adding ? "Adding..." : "Add Question"}
                </button>
              </div>
            </form>
          </div>
        </div>
  );
}